function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import * as React from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import range from 'lodash/range';
import { FloatingOverlay } from '@floating-ui/react';
import { h } from 'snabbdom';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ChangeEvent } from 'common/components/select/types';
import { DEFAULT_CHART_HEIGHT, ProjectFeatureFlag } from 'common/report/constants';
import { withStyles } from 'common/util/react';
import { ItemClickEvent, ItemsMenuTheme, Section, SectionItem, TooltipInfo } from 'common/components/items-menu/types';
import { useSegmentFilterController } from 'common/contexts/react/segment-filter-controller-context';
import { useTranslation } from 'common/i18n/hooks';
import { SegmentColorPicker } from 'common/query-builder/chart-customization/colors/segment-color-picker';
import { useFeatureFlag } from 'common/hooks/useFeatureFlag';
import { useTours } from 'common/contexts/react/tours-context';
import { isElementInViewport } from 'common/util/dom';
import { useQueryContextManager } from 'common/contexts/react/query-context-manager';
import { TooltipAttrs } from '../tooltip/types';
import { InputReact } from '../input';
import { ChartTooltip } from '../chart-tooltip';
import { ItemsMenuReact } from '../items-menu';
import { ButtonReact } from '../button';
import { ButtonSize, ButtonTheme } from '../button/constants';
import { ChartLegendTheme } from './constants';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import { ChartLegendItem, rollUpExtraItemsIntoOther } from './utils';
export interface ChartLegendProps {
  className?: string;
  backgroundColor?: string;
  enableSearch?: boolean;
  sections?: Section[];
  segments?: string[];
  showSelectAll?: boolean;
  height?: string;
  wrapLabels?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  maxLegendItems?: number;
  maxTableItemWidth?: string;
  theme?: ChartLegendTheme;
  selectAllDisabled?: boolean;
  selectAllTooltip?: TooltipInfo;
  tooltip?: TooltipAttrs;
  onToggleCheckbox?: (toggle: {
    hiddenSeries: string[];
    isSelectAll: boolean;
    item?: SectionItem<{
      sectionIdx: number;
      segmentId: string;
    }>;
  }) => void;
  /** whether legend items should be left-justified (used by metric charts when main metric is left justified) */
  justifyStart?: boolean;
}
const SEARCH_HEIGHT = 56;
const ChartLegend = React.forwardRef<HTMLDivElement, ChartLegendProps>(function (props, outerRef) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  React.useImperativeHandle(outerRef, () => containerRef.current!, []);
  const tours = useTours();
  const queryContextManager = useQueryContextManager();
  const canClickItem = useFeatureFlag(ProjectFeatureFlag.ConsistentColors) && queryContextManager.canEditBoard;
  const {
    print
  } = useTranslation();
  const segmentFilterController = useSegmentFilterController();
  const inputRef = React.useRef(null);
  const itemsRefs = React.useRef<HTMLElement[]>([]);
  const overflowTooltipParentRef = React.useRef<HTMLDivElement>(null);
  const [hiddenSeries, setHiddenSeries] = React.useState<string[]>([]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [showChartTooltip, setShowChartTooltip] = React.useState(false);
  /** Encapsulates 3 mutually exclusive active item states: no active item; single regular active
   * item; single active item that is in the overflow menu */
  const [active, setActive] = React.useState<{
    index: number;
    inOverflowMenu: boolean;
  } | null>(null);
  const [overflowMenuOpen, setOverflowMenuOpen] = React.useState(false);
  React.useEffect(() => {
    var _props$sections$0$ite, _props$sections;
    if (props.theme === ChartLegendTheme.CardTable && canClickItem && ((_props$sections$0$ite = (_props$sections = props.sections) === null || _props$sections === void 0 || (_props$sections = _props$sections[0]) === null || _props$sections === void 0 || (_props$sections = _props$sections.items) === null || _props$sections === void 0 ? void 0 : _props$sections.length) !== null && _props$sections$0$ite !== void 0 ? _props$sections$0$ite : 0) > 1 && isElementInViewport(containerRef.current as HTMLElement)) {
      tours.openTour("consistent-colors", containerRef.current);
    }
  }, [props.sections, props.theme, canClickItem, props.loading]);
  const handleToggledAllCheckboxes = () => {
    var _props$segments;
    const newHiddenSeries = hiddenSeries.length > 0 ? [] : (_props$segments = props.segments) !== null && _props$segments !== void 0 ? _props$segments : [];
    setHiddenSeries(newHiddenSeries);
    if (props.onToggleCheckbox) {
      props.onToggleCheckbox({
        hiddenSeries: newHiddenSeries,
        isSelectAll: true
      });
    }
  };
  const handleToggledCheckbox = (ev: ItemClickEvent) => {
    const item = ev.detail.item;
    const originalHiddenSeries = hiddenSeries;
    let newHiddenSeries = originalHiddenSeries;
    if (originalHiddenSeries.includes(print(item.label))) {
      newHiddenSeries = originalHiddenSeries.filter(value => value !== item.label);
    } else {
      newHiddenSeries.push(print(item.label));
    }
    setHiddenSeries(newHiddenSeries);
    if (props.onToggleCheckbox) {
      props.onToggleCheckbox({
        item,
        hiddenSeries: newHiddenSeries,
        isSelectAll: false
      });
    }
  };
  const handleUpdateLegendSearch = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(ev.target.value.toLowerCase());
  };
  const getItemsMenuHeight = (): string => {
    const fromTop = props.enableSearch ? SEARCH_HEIGHT : 0;
    const height = props.height || DEFAULT_CHART_HEIGHT;
    return "calc(".concat(height, " - ").concat(fromTop, "px)");
  };
  function LegendLoader() {
    return <div className='loader' data-sentry-component="LegendLoader" data-sentry-source-file="index.tsx">
        {range(9).map((_, idx) => <div className='mp-skeleton-large' key={idx} />)}
      </div>;
  }
  const onSegmentMouseOverChange = (item: ChartLegendItem, mouseOver: boolean) => {
    segmentFilterController.notifySeriesHovered(mouseOver ? {
      seriesId: item.value,
      source: "legend"
    } : null);
  };
  const onLegendItemClick = function onLegendItemClick(ev: React.MouseEvent<HTMLDivElement> | MouseEvent, itemIndex: number) {
    let isOverflowItem = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (!canClickItem) {
      return;
    }
    ev.stopPropagation();
    setActive({
      index: itemIndex,
      inOverflowMenu: isOverflowItem
    });
    tours.dismissTour({
      tour: "consistent-colors"
    });
  };

  // Only allow one section for card legends
  let themedLegend = null;
  if (props.theme === ChartLegendTheme.CardTable) {
    var _items$at, _colorPickerItem$valu;
    let items: ChartLegendItem[] = props.sections![0].items!;
    items = rollUpExtraItemsIntoOther(items, props.maxLegendItems);
    const activeItemIndex = active !== null && active !== void 0 && active.inOverflowMenu ? null : active === null || active === void 0 ? void 0 : active.index;
    const activeItem = items[activeItemIndex!];
    const activeOverflowItem = active !== null && active !== void 0 && active.inOverflowMenu ? (_items$at = items.at(-1)) === null || _items$at === void 0 || (_items$at = _items$at.tooltipSections) === null || _items$at === void 0 ? void 0 : _items$at[active === null || active === void 0 ? void 0 : active.index] : null;
    const colorPickerItem = !(activeItem !== null && activeItem !== void 0 && activeItem.tooltipSections) && activeItem || activeOverflowItem;
    themedLegend = <div className={styles.legendContainer}>
        <div className={clsx(styles.legendLine, {
        [styles.justifyStart]: props.justifyStart
      })}>
          {items.map((item, itemIndex) => {
          var _props$maxTableItemWi;
          const labelString = print(item.label);
          const isOverflowButton = item.tooltipSections;
          return isOverflowButton ? <div className={styles.overflowButtonWrapper} key={"".concat(itemIndex, "-").concat(labelString)}>
                <SelectReact ref={el => itemsRefs.current[itemIndex] = el as HTMLElement} label={item.label as string} sections={[{
              items: item.tooltipSections!.map(subItem => _objectSpread(_objectSpread({}, subItem), {}, {
                isSelected: false
              })),
              iconRenderer: subItem =>
              // @ts-ignore "No overload matches this call"
              h("div", {
                style: {
                  backgroundColor: subItem.color,
                  borderRadius: "2px",
                  height: "10px",
                  width: "10px"
                }
              })
            }]} theme={SelectTheme.QbSubitem} size={ButtonSize.Small} listTheme={ItemsMenuTheme.Small} disableSearch menuWidth={-1} title='' disabled={!canClickItem} disabledStaysOpaque onMouseEnter={() => !overflowMenuOpen && setShowChartTooltip(true)} onMouseOut={ev => {
              var _ev$relatedTarget;
              // hide the overflow tooltip unless the pointer is on the tooltip
              if (!(ev !== null && ev !== void 0 && (_ev$relatedTarget = ev.relatedTarget as Element) !== null && _ev$relatedTarget !== void 0 && _ev$relatedTarget.querySelector("[data-overflow-tooltip]"))) {
                setShowChartTooltip(false);
              }
            }} onMenuChange={ev => {
              // select was opened or closed
              setOverflowMenuOpen(ev.detail.isOpen);
              if (ev.detail.isOpen) {
                setShowChartTooltip(false);
              }
            }} onChange={ev => {
              // item was selected
              const index = item.tooltipSections!.findIndex(subItem => subItem.value === (ev as ChangeEvent).detail.item.value);
              setActive({
                index,
                inOverflowMenu: true
              });
              tours.dismissTour({
                tour: "consistent-colors"
              });
            }} />
                <div ref={overflowTooltipParentRef} className={styles.overflowTooltipTarget} />
              </div> : <ButtonReact ref={el => itemsRefs.current[itemIndex] = el as HTMLElement} className={clsx(styles.legendSegment, {
            [styles.clickable]: canClickItem
          }, "truncated")} theme={ButtonTheme.Tertiary} size={ButtonSize.Inline} onMouseEnter={() => onSegmentMouseOverChange(item, true)} onMouseLeave={() => onSegmentMouseOverChange(item, false)} key={"".concat(itemIndex, "-").concat(labelString)} title={labelString} pressed={activeItemIndex === itemIndex || isOverflowButton && Boolean(activeOverflowItem)} style={{
            maxWidth: (_props$maxTableItemWi = props.maxTableItemWidth) !== null && _props$maxTableItemWi !== void 0 ? _props$maxTableItemWi : ""
          }} onClick={canClickItem ? ev => onLegendItemClick(ev, itemIndex) : () => {}}>
                {item.label ? <div className={styles.segmentText} style={{
              '--color': item.color
            }}>
                    {labelString}
                  </div> : null}
              </ButtonReact>;
        })}
        </div>
        {showChartTooltip ?
      // overflow tooltip with the remaining legend items
      <div onMouseLeave={() => setShowChartTooltip(false)}>
            <ChartTooltip positioning={{
          positionParent: overflowTooltipParentRef.current as HTMLElement
        }} pointerEvents='auto'>
              <div data-overflow-tooltip>
                {/* @ts-ignore Object is possibly 'undefined' */}
                {items.at(-1).tooltipSections.map((subItem, index) => <div className={styles.segmentText} key={index} style={{
              '--color': subItem.color
            }} onMouseEnter={() => onSegmentMouseOverChange(subItem, true)} onMouseLeave={() => onSegmentMouseOverChange(subItem, false)}>
                    {print(subItem.label)}
                  </div>)}
              </div>
            </ChartTooltip>
          </div> : null}
        {colorPickerItem && createPortal(<>
              <FloatingOverlay>
                <SegmentColorPicker path={(_colorPickerItem$valu = colorPickerItem.value) !== null && _colorPickerItem$valu !== void 0 ? _colorPickerItem$valu : colorPickerItem.label} segmentColor={colorPickerItem.color!}
          // if it's an overflow item that's active, position the color picker relative to the last (overflow) legend item button
          positionParent={itemsRefs.current[activeItemIndex !== null && activeItemIndex !== void 0 ? activeItemIndex : items.length - 1]} onClose={() => {
            setActive(null);
          }} />
              </FloatingOverlay>
            </>, window.document.querySelector("#modals")!)}
      </div>;
  } else {
    themedLegend = <div className={styles.container}>
        <ItemsMenuReact style={{
        maxHeight: getItemsMenuHeight()
      }} elref={"itemsMenu"} multiple searchFilter={searchQuery} showSelectAll={props.showSelectAll} selectAllDisabled={props.selectAllDisabled} selectAllTooltip={props.selectAllTooltip} onHoverItem={(item: SectionItem) => {
        var _item$value;
        if (((_item$value = item.value) === null || _item$value === void 0 ? void 0 : _item$value.sectionIdx) === 0) {
          segmentFilterController.notifySeriesHovered({
            seriesId: item.label as string
          });
        } else {
          segmentFilterController.notifySeriesHovered(null);
        }
      }} onHoverOut={() => {
        segmentFilterController.notifySeriesHovered(null);
      }} sections={props.sections}
      // @ts-ignore `Type 'undefined' is not assignable to type 'CustomEvent ...`
      onClickItem={handleToggledCheckbox} onSelectAll={handleToggledAllCheckboxes} keydownProxyEl={inputRef.current!} />
      </div>;
  }
  return <div ref={containerRef} elref='wrapper' className={clsx(props.theme === ChartLegendTheme.CardTable ? styles.chartThemeTable : styles.chartThemePrimary, props.className)}>
      {props.theme === ChartLegendTheme.Primary && props.enableSearch ? <div className={styles.search}>
          <InputReact ref={inputRef} icon='search' placeholder='Search' elref={"search"} onInput={handleUpdateLegendSearch} />
        </div> : null}
      {props.loading ? <LegendLoader /> : themedLegend}
    </div>;
});
export const ChartLegendReact = withStyles(ChartLegend, css);